import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCoffee } from "../hooks/useCoffees";
import { api } from "../lib/api";
import { FlavorTags } from "../components/FlavorTags";
import { GradeBadge } from "../components/GradeBadge";
import { PortionCounter } from "../components/PortionCounter";
import { CoffeeForm } from "../components/CoffeeForm";
import { PhotoGallery } from "../components/PhotoGallery";
import { BrewLogSection } from "../components/BrewLogSection";
import { BeanSizeSection } from "../components/BeanSizeSection";
import type { PortionLogEntry, Impression, BrewFeedEntry } from "../types";
import { isTea, kindLabel } from "../types";

export function CoffeeDetail() {
  const { id } = useParams();
  const numId = Number(id);
  const nav = useNavigate();
  const { data, refresh, setData } = useCoffee(Number.isFinite(numId) ? numId : null);
  const [log, setLog] = useState<PortionLogEntry[]>([]);
  const [impressions, setImpressions] = useState<Impression[]>([]);
  const [brewFeed, setBrewFeed] = useState<BrewFeedEntry[]>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (Number.isFinite(numId)) api.log(numId).then(setLog).catch(() => {});
  }, [numId, data?.portions]);
  useEffect(() => {
    if (Number.isFinite(numId)) api.listImpressions(numId).then(setImpressions).catch(() => {});
  }, [numId]);
  // Cross-coffee brew history powers the cold-start recipe suggestion for coffees not yet brewed.
  useEffect(() => {
    api.brews().then(setBrewFeed).catch(() => {});
  }, [numId]);

  if (!data) return <p className="text-paper-500">Loading…</p>;

  const onUse = async () => { setData(await api.use(data.id)); };
  const onAdjust = async (delta: number) => {
    if (delta < 0) return onUse();
    setData(await api.update(data.id, { portions: data.portions + delta }));
  };
  const tea = isTea(data.kind);
  const onDelete = async () => {
    if (!confirm(tea ? "Archive this tea?" : "Archive this coffee?")) return;
    await api.remove(data.id);
    nav("/browse");
  };
  const onDestroy = async () => {
    const noun = tea ? "tea" : "coffee";
    if (!confirm(
      `Permanently delete this ${noun}?\n\n"${data.variety}" and its entire brew log, portion history, and photos will be erased. This cannot be undone.`
    )) return;
    await api.destroy(data.id);
    nav("/browse");
  };

  if (editing) {
    return (
      <div className="space-y-6">
        <div>
          <div className="eyebrow">Editing</div>
          <h2 className="font-display text-3xl">{data.variety}</h2>
        </div>
        <CoffeeForm
          initial={data}
          submitLabel="Save changes"
          onCancel={() => setEditing(false)}
          onSubmit={async (input) => {
            await api.update(data.id, input);
            setEditing(false);
            refresh();
          }}
        />
      </div>
    );
  }

  return (
    <article className="space-y-12">
      <div className="grid md:grid-cols-[260px_1fr] gap-10 items-start">
        <PhotoGallery coffee={data} onChange={setData} />

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="eyebrow flex items-center gap-3">
              <span>{data.country}{data.region ? ` · ${data.region}` : ""}</span>
              {data.kind && data.kind !== "coffee" ? (
                <span className="text-[10px] tracking-widest text-paper-500">{kindLabel(data.kind).toUpperCase()}</span>
              ) : null}
            </div>
            <h1 className="font-display text-5xl tracking-tight leading-none">{data.variety}</h1>
            <p className="text-paper-600">
              {tea
                ? `${data.roaster}${data.roaster_city ? ` · ${data.roaster_city}` : ""}`
                : `${data.process} · ${data.roaster}${data.roaster_city ? ` · ${data.roaster_city}` : ""}`}
            </p>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <PortionCounter
              portions={data.portions}
              onUse={onUse}
              onAdjust={onAdjust}
              unitLabel={tea ? "Use one bottle" : "Use one"}
            />
            <span className="text-xs text-paper-500 uppercase tracking-widest">
              {tea ? `bottle${data.portions === 1 ? "" : "s"}` : `× ${data.grams}g`}
            </span>
          </div>

          <GradeBadge grade={data.grade} score={data.award_score} />
          {data.award_detail ? <p className="text-sm text-paper-700 italic">{data.award_detail}</p> : null}
          <FlavorTags tags={data.flavor_tags} />

          {(data.aroma_notes || data.finish_notes) ? (
            <div className="text-sm text-paper-700 space-y-0.5">
              {data.aroma_notes ? <div><span className="eyebrow mr-2">Aroma</span>{data.aroma_notes}</div> : null}
              {data.finish_notes ? <div><span className="eyebrow mr-2">Finish</span>{data.finish_notes}</div> : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-4">
            <button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>
            <Link to={`/print/${data.id}`} className="btn-ghost">Print one label</Link>
            <Link to={`/labels?ids=${data.id}`} className="btn-ghost">Add to label sheet</Link>
            <button className="btn-ghost ml-auto" onClick={onDelete}>Archive</button>
            <button className="btn-danger" onClick={onDestroy}>Delete permanently</button>
          </div>
        </div>
      </div>

      <section className="grid md:grid-cols-2 gap-px bg-paper-200 border-y border-paper-200">
        <Block title="Origin" rows={[
          [tea ? "Garden" : "Farm", data.farm],
          [tea ? "Tea master" : "Farmer", data.farmer],
          ["MASL", data.masl != null ? `${data.masl} m` : null],
          ["Lot", data.lot_number],
        ]} />
        <Block title={tea ? "Producer" : "Roaster"} rows={[
          [tea ? "Producer" : "Roaster", data.roaster],
          ["City", data.roaster_city],
          [tea ? "SKU / Tin" : "SKU", data.roaster_sku],
          [tea ? "Production date" : "Roast date", data.roast_date],
          [tea ? "Storage date" : "Freeze date", data.freeze_date],
        ]} />
        {data.brew_recipe ? (
          <div className="md:col-span-2">
            <Block title="Brew recipe" rows={[
              ["Method", data.brew_recipe.method ?? null],
              ["Ratio", data.brew_recipe.ratio ?? null],
              ["Temp", data.brew_recipe.temp_c != null ? `${data.brew_recipe.temp_c}°C` : null],
              ["Time", data.brew_recipe.time_s != null ? `${data.brew_recipe.time_s}s` : null],
              ["Notes", data.brew_recipe.notes ?? null],
            ]} />
          </div>
        ) : null}
      </section>

      {data.story ? (
        <section>
          <div className="eyebrow mb-2">Story</div>
          <p className="whitespace-pre-wrap text-paper-800 text-[15px] leading-relaxed max-w-2xl">{data.story}</p>
        </section>
      ) : null}

      {data.personal_notes ? (
        <section>
          <div className="eyebrow mb-2">Notes</div>
          <p className="whitespace-pre-wrap text-paper-800 text-[15px] leading-relaxed max-w-2xl">{data.personal_notes}</p>
        </section>
      ) : null}

      <BrewLogSection
        coffee={data}
        impressions={impressions}
        setImpressions={setImpressions}
        brewFeed={brewFeed}
        onConsumed={(c) => { if (c) setData(c); }}
        onCoffeeChange={setData}
      />

      {!tea ? <BeanSizeSection coffee={data} onCoffeeChange={setData} /> : null}

      <section>
        <div className="eyebrow mb-3">Portion history · {log.length}</div>
        {log.length === 0 ? (
          <p className="text-sm text-paper-500">No portions used yet.</p>
        ) : (
          <ul className="border-t border-paper-200 text-sm">
            {log.map((e) => (
              <li key={e.id} className="py-2 border-b border-paper-200 flex justify-between">
                <span>{new Date(e.logged_at + "Z").toLocaleString()}</span>
                <span className="text-paper-500">{e.note || "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}

function Block({ title, rows }: { title: string; rows: [string, string | number | null][] }) {
  const filled = rows.filter(([, v]) => v != null && v !== "");
  if (filled.length === 0) {
    return (
      <div className="bg-paper-50 px-6 py-6">
        <div className="eyebrow mb-3">{title}</div>
        <p className="text-sm text-paper-400">—</p>
      </div>
    );
  }
  return (
    <div className="bg-paper-50 px-6 py-6">
      <div className="eyebrow mb-4">{title}</div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
        {filled.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-paper-500">{k}</dt>
            <dd className="text-ink">{String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
